import { useMemo } from 'react'

const FONT_SIZE = 13
const ROTATION_DEG = -28
// Largura média de um caractere Arial bold, com folga para não cortar o texto
const CHAR_WIDTH_RATIO = 0.65
// Espaço livre entre uma repetição e outra
const TILE_PADDING_X = 48
const TILE_PADDING_Y = 36

/**
 * Opacidade da marca d'água. Em ~0.04 ela fica praticamente imperceptível na leitura, mas é
 * recuperável num print vazado aumentando contraste/brilho. Suba o valor para torná-la visível
 * (dissuasão) — ex: 0.15.
 */
const WATERMARK_OPACITY = 0.05

const escapeXml = (value: string) =>
	value
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&apos;')

// O tile acompanha o tamanho do texto girado; com tamanho fixo o SVG cortava matrícula e data.
const getTileSize = (text: string) => {
	const textWidth = text.length * FONT_SIZE * CHAR_WIDTH_RATIO
	const radians = (Math.abs(ROTATION_DEG) * Math.PI) / 180
	const rotatedWidth =
		textWidth * Math.cos(radians) + FONT_SIZE * Math.sin(radians)
	const rotatedHeight =
		textWidth * Math.sin(radians) + FONT_SIZE * Math.cos(radians)

	return {
		width: Math.ceil(rotatedWidth + TILE_PADDING_X),
		height: Math.ceil(rotatedHeight + TILE_PADDING_Y),
	}
}

// Camada clara + camada escura: a marca é recuperável tanto sobre conteúdo claro quanto sobre o vídeo.
const buildTile = (text: string) => {
	const label = escapeXml(text)
	const { width, height } = getTileSize(text)
	const transform = `rotate(${ROTATION_DEG} ${width / 2} ${height / 2})`
	const common = `x='50%' y='50%' text-anchor='middle' dominant-baseline='middle' font-family='Arial, sans-serif' font-size='${FONT_SIZE}' font-weight='700' transform='${transform}'`
	const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${width}' height='${height}'><text ${common} dx='1' dy='1' fill='#ffffff' fill-opacity='${WATERMARK_OPACITY}'>${label}</text><text ${common} fill='#000000' fill-opacity='${WATERMARK_OPACITY}'>${label}</text></svg>`
	return `url("data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}")`
}

interface NoticeWatermarkProps {
	badge: string
}

/**
 * Marca d'água com identificação do leitor. Não impede captura de tela — o objetivo é
 * identificar a origem em caso de vazamento. Deve ficar dentro de um container `relative`.
 */
export function NoticeWatermark({ badge }: NoticeWatermarkProps) {
	const backgroundImage = useMemo(() => {
		const timestamp = new Date().toLocaleString('pt-BR', {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
		})
		return buildTile(`${badge} · ${timestamp}`)
	}, [badge])

	return (
		<div
			aria-hidden="true"
			className="pointer-events-none absolute inset-0 z-10 select-none"
			style={{ backgroundImage, backgroundRepeat: 'repeat' }}
		/>
	)
}
